/**
 * Redirect page component
 * 
 * Secured route that shows a loading spinner and redirects to a URL
 * provided via query parameter, appending the userId and accessCode to the URL.
 */

import React, { useEffect } from "react";
import { useSearchParams } from "react-router-dom";

import { BrandNeutralLoading } from "common/NoomLoader";

import { useAuthCredentials } from "../hooks/useAuthCredentials";
import { usePatient } from "../hooks/usePatient";

export const RedirectPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const patient = usePatient();
  const { accessCode } = useAuthCredentials();
  const redirectUrl = searchParams.get("redirectUrl");
  const sha = searchParams.get("sha");

  useEffect(() => {
    if (!redirectUrl) {
      // If no URL provided, redirect to home
      window.location.href = "/";
      return;
    }

    // Decode the URL-encoded redirect URL
    const decodedUrl = decodeURIComponent(redirectUrl);
    
    if (!patient?.user_id) {
      // If no userId available, redirect without userId param
      window.location.href = decodedUrl;
      return;
    }

    // Parse the URL and append userId, accessCode, and sha (if present)
    try {
      // Try to parse as absolute URL first
      const url = new URL(decodedUrl, window.location.origin);
      url.searchParams.set("userId", patient.user_id);
      
      // Add accessCode if available
      if (accessCode) {
        url.searchParams.set("accessCode", accessCode);
      }
      
      // Add sha if it exists in current page's query params
      if (sha) {
        url.searchParams.set("sha", sha);
      }
      
      window.location.href = url.toString();
    } catch {
      // If URL parsing fails, treat as relative path and append params
      const separator = decodedUrl.includes("?") ? "&" : "?";
      const params = new URLSearchParams();
      params.set("userId", patient.user_id);
      
      if (accessCode) {
        params.set("accessCode", accessCode);
      }
      
      if (sha) {
        params.set("sha", sha);
      }
      
      window.location.href = `${decodedUrl}${separator}${params.toString()}`;
    }
  }, [redirectUrl, patient?.user_id, accessCode, sha]);

  // Show loading spinner while processing redirect
  return <BrandNeutralLoading />;
};

